import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { Plus, Search } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Moeda, formatMoeda } from '../../utils/orcamentoPagamento';
import { ActionButton, Button, Pagination } from '../../components/UI';

interface VendaItem {
  _id: string;
  descricao: string;
  valorTotal: number;
  moeda?: Moeda;
  formaPagamento: string;
  status: 'aberta' | 'concluida' | 'cancelada';
  dataVenda: string;
  clienteId: { _id: string; nome: string };
}

const STATUS_VALUES = ['aberta', 'concluida', 'cancelada'];

const STATUS_LABELS: { [key: string]: string } = {
  aberta: 'Aberta',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

const STATUS_COLORS: { [key: string]: string } = {
  aberta: 'bg-blue-100 text-blue-800',
  concluida: 'bg-green-100 text-green-800',
  cancelada: 'bg-red-100 text-red-800'
};

interface VendasProps {
  embedded?: boolean;
}

const Vendas: React.FC<VendasProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [vendas, setVendas] = useState<VendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchVendas = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus }),
      });

      const response = await axios.get(`/vendas?${params}`);
      setVendas(response.data.vendas);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedStatus, currentPage]);

  useEffect(() => {
    fetchVendas();
  }, [fetchVendas]);

  const handleDelete = async (vendaId: string) => {
    if (!(await confirm({ title: 'Excluir venda', message: 'Esta venda será excluída permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/vendas/${vendaId}`);
      setVendas(vendas.filter((venda) => venda._id !== vendaId));
      notifications.success.vendaDeleted();
    } catch (error: any) {
      console.error('Erro ao excluir venda:', error);
      notifications.error.vendaDelete(error.response?.data?.message || 'Erro ao excluir venda');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Ações da linha: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (venda: (typeof vendas)[number]) => (
    <div className="row-actions md:justify-end">
      <ActionButton
        action="edit"
        to={`/admin/vendas/${venda._id}/edit`}
      />
      <ActionButton
        action="delete"
        onClick={() => handleDelete(venda._id)}
      />
    </div>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {!embedded && (
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('admin.vendas')}</h1>
                <p className="text-gray-600">{t('admin.vendasDesc')}</p>
              </div>
              <Button to="/admin/vendas/new">
                <Plus className="w-5 h-5" />
                <span>Nova venda</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8'}>
        {embedded && (
          <div className="flex justify-end mb-4">
            <Button to="/admin/vendas/new">
              <Plus className="w-5 h-5" />
              <span>Nova venda</span>
            </Button>
          </div>
        )}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="field pl-10 pr-4"
                />
              </div>
            </div>

            <div className="lg:w-56">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="field field--select"
              >
                <option value="">Todos os status</option>
                {STATUS_VALUES.map((status) => (
                  <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : vendas.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma venda encontrada</h3>
              <Button to="/admin/vendas/new">Registrar Primeira Venda</Button>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Forma</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendas.map((venda) => (
                    <tr key={venda._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {venda.clienteId ? (
                          <Link to={`/admin/clientes/${venda.clienteId._id}`} className="text-sm text-primary-600 hover:text-primary-800">
                            {venda.clienteId.nome}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{venda.descricao}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatMoeda(venda.valorTotal, venda.moeda)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{venda.formaPagamento}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[venda.status]}`}>
                          {STATUS_LABELS[venda.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(venda.dataVenda)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(venda)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {vendas.map((venda) => (
                <li key={venda._id} className="p-4 space-y-3">
                  <div className="min-w-0">
                    {venda.clienteId ? (
                      <Link to={`/admin/clientes/${venda.clienteId._id}`} className="text-sm text-primary-600 hover:text-primary-800">
                        {venda.clienteId.nome}
                      </Link>
                    ) : '—'}
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Descrição</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {venda.descricao}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Valor</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {formatMoeda(venda.valorTotal, venda.moeda)}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Forma</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {venda.formaPagamento}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[venda.status]}`}>
                          {STATUS_LABELS[venda.status]}
                        </span>
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Data</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {formatDate(venda.dataVenda)}
                      </dd>
                    </div>
                  </dl>
                  {renderActions(venda)}
                </li>
              ))}
            </ul>
            </>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
        </div>
      </div>
    </div>
  );
};

export default Vendas;
